import TRAIN_ICON_N from "../../assets/train-n.png";
import TRAIN_ICON_E from "../../assets/train-e.png";
import TRAIN_ICON_W from "../../assets/train-w.png";
// ─── Design Tokens ───────────────────────────────────────────────────────
// Single source of truth for the Via Fera visual language.
// Every color, shadow, font, and icon string lives here.
// Leaflet DivIcons need raw HTML — this module provides pre-built strings
// for both "normal" (white-on-color) and "inverted" (color-on-white) variants.
//
import {
  ArrowDownUp, ArrowRight, BedDouble, Bookmark, BookmarkCheck, Calendar, Car, Castle, Check,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Coffee,
  Compass, Copy, Download, Filter, FolderOpen, Gem, Globe, Heart, Landmark, Leaf,
  Link2, List, Loader2, LogIn, LogOut, MapPin, Minus, Moon,
  Pencil, Plane, Plus, RefreshCw, Route, Ruler, Search, Share2,
  Sparkles, Ticket, Train, TrainFront, Trash2, TreePine, TrendingDown,
  TrendingUp, TriangleAlert, User, Utensils, Wallet, X,
  type LucideIcon,
} from "lucide-react";

// Re-export all icons — components must import from here, never from lucide-react directly.
// This makes design-tokens the single auditable registry of every icon in the app.
export {
  ArrowDownUp, ArrowRight, BedDouble, Bookmark, BookmarkCheck, Calendar, Car, Castle, Check,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Coffee,
  Compass, Copy, Download, Filter, FolderOpen, Gem, Globe, Heart, Landmark, Leaf,
  Link2, List, Loader2, LogIn, LogOut, MapPin, Minus, Moon,
  Pencil, Plane, Plus, RefreshCw, Route, Ruler, Search, Share2,
  Sparkles, Ticket, Train, TrainFront, Trash2, TreePine, TrendingDown,
  TrendingUp, TriangleAlert, User, Utensils, Wallet, X,
  type LucideIcon,
} from "lucide-react";
// DESIGN DECISIONS (read before changing anything):
//
// 1. DAY_COLORS (defined below) cycles through 7 route colors. We choose POI/badge
//    colors that don't collide with ANY of them. Heritage landmark uses amber
//    #f59e0b — safe because Day 2 was changed from amber to rose #f43f5e.
//
// 2. POI_BADGE_COLORS was removed — it was an identical duplicate of POI_COLORS.
//    The old workaround (heritage badge = indigo #4f46e5) is no longer needed.
//
// 3. Hidden-gem uses sky #0ea5e9 everywhere — dot, badge, connector line, and
//    SearchPanel list dot. The old gold #FFD700 lineColor and violet #a78bfa
//    SearchPanel dot were incorrect.
//
// 4. Neutral grays follow the Tailwind scale exclusively. No #555/#888/#666/#999.
//    title=#1a1a2e (brand dark), subtitle=#374151, secondary=#6b7280, tertiary=#9ca3af.
//
// 5. Font stack "Inter,system-ui,sans-serif" is defined once, imported everywhere.
//
// 6. Scenic routes use teal #0d9488 — the old goldenrod #DAA520 was too close
//    to heritage amber #f59e0b. Teal evokes rivers/nature and sits in a free
//    hue slot between cyan Day 6 and green natural-landmark.
//
// 7. Discovery/sparkle uses teal #0d9488 — a cool-toned color
//    clearly distinct from heritage amber #f59e0b and food red #ef4444,
//    evoking exploration without clashing with warm POI hues.

// ─── Neutral Palette ─────────────────────────────────────────────────────
// Brand dark + Tailwind gray scale for text and backgrounds.

export const NEUTRAL = {
  /** Near-black brand dark — titles, city names, headings */
  title: "#1a1a2e",
  /** gray-700 — subtitle text, departure/arrival times */
  subtitle: "#374151",
  /** gray-500 — secondary labels, descriptions */
  secondary: "#6b7280",
  /** gray-400 — tertiary labels, operators, faint annotations */
  tertiary: "#9ca3af",
  /** Light blue-gray — map container background */
  mapBg: "#f0f4f8",
} as const;

// ─── Typography ──────────────────────────────────────────────────────────

export const FONT_STACK = "Inter,system-ui,sans-serif";

// ─── Day / Route Colors ──────────────────────────────────────────────────
// 7-colour cycle for multi-day trip segments. Consumed by MapView, computeDaySegments,
// useTripPlanner. Chosen to avoid collision with POI_COLORS below.

export const DAY_COLORS = [
  "#6366f1", // indigo
  "#f43f5e", // rose
  "#10b981", // emerald
  "#ef4444", // red
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#f97316", // orange
];

// ─── Stop Action Colors ──────────────────────────────────────────────────

export const ACTION_COLORS = {
  overnight: "#7c3aed",
  fika: "#92400e",
  food: "#ef4444",
  default: "#818cf8",
} as const;

export const DISCOVERY_COLOR = "#0d9488";

export const POI_COLORS = {
  "heritage-landmark": "#f59e0b",
  "hidden-gem": "#0ea5e9",
  castle: "#6b7280",
  "natural-landmark": "#16a34a",
  discovery: DISCOVERY_COLOR,
} as const;

// ─── SVG Paths (Lucide-compatible) ──────────────────────────────────────

const LANDMARK_PATH = `<line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>`;
const GEM_PATH = `<polygon points="6 3 18 3 22 9 12 22 2 9"/>`;
const GEM_DETAIL = `<line x1="2" y1="9" x2="22" y2="9" stroke="rgba(0,0,0,0.2)"/><line x1="12" y1="3" x2="6" y2="9" stroke="rgba(0,0,0,0.2)"/><line x1="12" y1="3" x2="18" y2="9" stroke="rgba(0,0,0,0.2)"/>`;
const CASTLE_PATH = `<path d="M22 20v-9H2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z"/><path d="M18 11V4H6v7"/><path d="M15 22v-4a3 3 0 0 0-6 0v4"/><path d="M22 11V9"/><path d="M2 11V9"/><path d="M6 4V2"/><path d="M18 4V2"/><path d="M10 4V2"/><path d="M14 4V2"/>`;
const TREE_PATH = `<path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"/><path d="M12 22v-3"/>` ;
const SPARKLE_PATH = `<path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>`;
const MOON_PATH = `<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>`;
const COFFEE_PATH = `<path d="M10 2v2"/><path d="M14 2v2"/><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a2 2 0 1 1 0 4h-1"/><path d="M6 2v2"/>`;
const UTENSILS_PATH = `<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>`;





export const TRAIN_ICON_DIRECTIONS = {
  N:  TRAIN_ICON_N,
  NE: TRAIN_ICON_E,
  E:  TRAIN_ICON_E,
  SE: TRAIN_ICON_E,
  S:  TRAIN_ICON_N,
  SW: TRAIN_ICON_W,
  W:  TRAIN_ICON_W,
  NW: TRAIN_ICON_W,
};

function buildSvg(
  path: string,
  opts: { size?: number; stroke?: string; fill?: string; strokeWidth?: number } = {}
): string {
  const { size = 12, stroke = "white", fill = "none", strokeWidth = 2.5 } = opts;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
}

// ─── Icon Element ───────────────────────────────────────────────────────
// Unified icon lookup. All icon references go through here — never import
// Lucide directly in components.

const LUCIDE_ICONS: Record<string, LucideIcon> = {
  // ── POI type aliases (for dynamic lookup via iconElement(poiType, "lucide")) ──
  "heritage-landmark": Landmark,
  "hidden-gem":        Gem,
  "castle":            Castle,
  "natural-landmark":  TreePine,

  // ── Stop action aliases ──
  "food":              Utensils,
  "overnight":         Moon,
  "fika":              Coffee,
  "close":             X,

  // ── Standard Lucide names (kebab-case) ──
  "arrow-down-up":  ArrowDownUp,
  "arrow-right":    ArrowRight,
  "bed-double":     BedDouble,
  "bookmark":       Bookmark,
  "bookmark-check": BookmarkCheck,
  "calendar":       Calendar,
  "car":            Car,
  "check":          Check,
  "chevron-down":   ChevronDown,
  "chevron-left":   ChevronLeft,
  "chevron-right":  ChevronRight,
  "chevron-up":     ChevronUp,
  "clock":          Clock,
  "compass":        Compass,
  "copy":           Copy,
  "download":       Download,
  "filter":         Filter,
  "folder-open":    FolderOpen,
  "gem":            Gem,
  "globe":          Globe,
  "heart":          Heart,
  "landmark":       Landmark,
  "leaf":           Leaf,
  "link-2":         Link2,
  "list":           List,
  "loader-2":       Loader2,
  "log-in":         LogIn,
  "log-out":        LogOut,
  "map-pin":        MapPin,
  "minus":          Minus,
  "moon":           Moon,
  "pencil":         Pencil,
  "plane":          Plane,
  "plus":           Plus,
  "refresh-cw":     RefreshCw,
  "route":          Route,
  "ruler":          Ruler,
  "search":         Search,
  "share-2":        Share2,
  "sparkles":       Sparkles,
  "ticket":         Ticket,
  "train":          Train,
  "train-front":    TrainFront,
  "trash-2":        Trash2,
  "tree-pine":      TreePine,
  "trending-down":  TrendingDown,
  "trending-up":    TrendingUp,
  "triangle-alert": TriangleAlert,
  "user":           User,
  "utensils":       Utensils,
  "wallet":         Wallet,
  "x":              X,
};

/** Single icon manager — tell it what you want and what format. */
export function iconElement(key: string, format: "lucide"): LucideIcon | null;
export function iconElement(key: string, format: "svg", inverted?: boolean): string;
export function iconElement(key: string, format: "lucide" | "svg", inverted = false): LucideIcon | string | null {
  if (format === "lucide") return LUCIDE_ICONS[key] ?? null;
  // POI colors: inverted = colored stroke on transparent, normal = white stroke
  const poiC = (poiKey: string) => inverted ? POI_COLORS[poiKey as keyof typeof POI_COLORS] : "white";
  // Action colors: inverted = action-colored stroke, normal = white stroke
  const actC = (actKey: string) => inverted ? ACTION_COLORS[actKey as keyof typeof ACTION_COLORS] ?? ACTION_COLORS.default : "white";
  switch (key) {
    case "heritage-landmark": return buildSvg(LANDMARK_PATH, { stroke: poiC("heritage-landmark") });
    case "hidden-gem":        return buildSvg(GEM_PATH + GEM_DETAIL, { stroke: poiC("hidden-gem"), fill: poiC("hidden-gem"), strokeWidth: 1.5 });
    case "castle":            return buildSvg(CASTLE_PATH, { stroke: poiC("castle") });
    case "natural-landmark":  return buildSvg(TREE_PATH, { stroke: poiC("natural-landmark") });
    case "discovery":         return buildSvg(SPARKLE_PATH, { stroke: poiC("discovery"), fill: "none" });
    case "food":              return buildSvg(UTENSILS_PATH, { stroke: actC("food") });
    case "overnight":         return buildSvg(MOON_PATH, { stroke: actC("overnight"), fill: actC("overnight") });
    case "fika":              return buildSvg(COFFEE_PATH, { stroke: actC("fika") });
    case "train-front":       return `<img src="${TRAIN_ICON_N}" width="80" height="42" style="display:block;pointer-events:none" />`;
    default:                  return "";
  }
}

// ─── POI Badge Config (Lucide icons + visual tokens) ────────────────────
// Used by React components (StopDiscoveryCard, etc.) for badge / reason rendering.

type POIBadgeConfig = { label: string; badgeBg: string; reason: { bg: string; border: string; text: string } };

const POI_BADGE_MAP: Record<string, POIBadgeConfig> = {
  "heritage-landmark": { label: "Heritage",   badgeBg: "bg-amber-500/90",  reason: { bg: "rgb(255 251 235)", border: "rgb(217 119 6 / 0.3)",  text: "#92400e" } },
  "hidden-gem":        { label: "Hidden Gem", badgeBg: "bg-sky-600/90",    reason: { bg: "rgb(240 249 255)", border: "rgb(2 132 199 / 0.3)",  text: "#0c4a6e" } },
  "castle":            { label: "Castle",     badgeBg: "bg-slate-600/90",  reason: { bg: "rgb(248 250 252)", border: "rgb(71 85 105 / 0.3)",  text: "#1e293b" } },
  "natural-landmark":  { label: "Landmark",   badgeBg: "bg-green-600/90",  reason: { bg: "rgb(240 253 244)", border: "rgb(22 163 74 / 0.3)",  text: "#14532d" } },
  "discovery":          { label: "Discovery",  badgeBg: "bg-teal-600/90",   reason: { bg: "rgb(240 253 250)", border: "rgb(13 148 136 / 0.3)", text: "#134e4a" } },
};

export function badgeElement(type: string): POIBadgeConfig | null {
  return POI_BADGE_MAP[type] ?? null;
}

/** Get the Lucide icon component + color for a POI type (for inline rendering). */
export function poiIconAndColor(poiType: string): { Icon: LucideIcon; color: string } {
  const Icon = LUCIDE_ICONS[poiType] ?? Gem;
  const color = POI_COLORS[poiType as keyof typeof POI_COLORS] ?? POI_COLORS["hidden-gem"];
  return { Icon, color };
}

// ─── Country Flags (Twemoji CDN) ────────────────────────────────────────
// Unicode flag emojis don't render on Windows. We use Twemoji SVGs instead
// so flags display identically on every OS. Country name → ISO 3166-1 alpha-2.

const COUNTRY_ISO: Record<string, string> = {
  France: "fr", Italy: "it", Germany: "de", Austria: "at",
  Switzerland: "ch", Spain: "es", Netherlands: "nl", Belgium: "be",
  "Czech Republic": "cz", Czechia: "cz", Poland: "pl", Hungary: "hu",
  Slovenia: "si", Croatia: "hr", Portugal: "pt", Denmark: "dk",
  Sweden: "se", Norway: "no", Slovakia: "sk", Finland: "fi",
  Romania: "ro", Bulgaria: "bg", Turkey: "tr",
  "United Kingdom": "gb", Greece: "gr", Serbia: "rs",
  Montenegro: "me", "Bosnia and Herzegovina": "ba",
  "North Macedonia": "mk", Albania: "al", Ireland: "ie",
  Luxembourg: "lu", Lithuania: "lt", Latvia: "lv", Estonia: "ee",
  Iceland: "is", Malta: "mt", Cyprus: "cy", Moldova: "md",
  Ukraine: "ua", Belarus: "by", Monaco: "mc", Liechtenstein: "li",
  Andorra: "ad", "San Marino": "sm", Kosovo: "xk",
};

/**
 * Returns an `<img>` tag for a country flag via Twemoji CDN.
 * Works on all platforms including Windows. Returns empty string if unknown.
 */
export function countryFlagImg(country: string, size = 20): string {
  const iso = COUNTRY_ISO[country];
  if (!iso) return "";
  // Twemoji encodes flags as regional indicator symbol pairs.
  // Each letter: 0x1F1E6 + (charCode - 97)  →  hex codepoint.
  const codepoints = iso
    .split("")
    .map((ch) => (0x1f1e6 + ch.codePointAt(0)! - 97).toString(16))
    .join("-");
  return `<img src="https://cdn.jsdelivr.net/gh/jdecked/twemoji@latest/assets/svg/${codepoints}.svg" alt="${iso.toUpperCase()}" width="${size}" height="${size}" style="vertical-align:middle;display:inline-block" />`;
}


